import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Trans } from 'react-i18next';

import { Button } from 'shared/components/Button';
import { getKlarnaEnabledCountry } from 'checkout/utils/orderUtils';
import { PaymentVendor } from 'checkout/enums/PaymentVendor';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { useLocation } from 'shared/hooks/useLocation';
import { GiftCard } from 'checkout/components/Payment/GiftCard';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { t } from 'localization/utils/t';
import { KlarnaPlacement } from 'checkout/components/Payment/KlarnaPlacement';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';
import { getRecommendedCurrency } from 'checkout/utils/getRecommendedCurrency';
import { useCurrency } from 'shared/hooks/useCurrency';
import { ChangeCurrencyKlarnaModal } from 'checkout/components/Payment/ChangeCurrencyKlarnaModal';

const TP = 'checkout.components.Payment.Klarna';

interface IKlarnaWindow {
  Payments: {
    // eslint-disable-next-line camelcase
    load: (options: { container: string; payment_method_category: string }) => void;
  };
  // OnsiteMessaging replaces window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' }) because will be deprecated in Q2 2023;
  // Find more info: https://docs.klarna.com/on-site-messaging/in-depth-knowledge/placements/
  OnsiteMessaging: {
    refresh: () => void;
  };
}

declare global {
  interface Window {
    Klarna: IKlarnaWindow;
  }
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'klarna-placement': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
}

interface IKlarnaProps {
  selectPaymentVendor: (selectedPaymentVendor: PaymentVendor) => void;
}

export const Klarna = ({ selectPaymentVendor }: IKlarnaProps) => {
  const {
    order: { localizedFinalPriceCents, number, addressId },
    createKlarnaSession,
    addresses,
    sections: {
      payment: { loading: isKlarnaLoading },
    },
  } = useCheckoutContext();
  const { user } = useStoreSelector((state) => state.auth);
  const { selectedCountryCode } = useLocation();
  const { selectedCurrencyCode } = useCurrency();
  const { availablePaymentMethods } = useCheckoutContext();
  const shippingCountryCode = addresses?.[addressId]?.countryCode;
  const [showChangeCurrencyModal, setShowChangeCurrencyModal] = useState(false);

  const countryCode = (() => {
    const selectedCountry = getKlarnaEnabledCountry(selectedCountryCode) && selectedCountryCode;
    const shippingCountry = getKlarnaEnabledCountry(shippingCountryCode) && shippingCountryCode;
    // Use users shop location as a fallback if no country is KlarnaEnabled
    return selectedCountry || shippingCountry || selectedCountryCode;
  })();

  const { region } =
    getKlarnaEnabledCountry(selectedCountryCode) ||
    getKlarnaEnabledCountry(shippingCountryCode) ||
    {};

  const shouldShowTagline = (() => {
    switch (region) {
      case 'CA':
      case 'NA':
      case 'EU':
      case 'UK':
        return true;
      default:
        return false;
    }
  })();

  const handleContinue = () => {
    const recommendedCurrency = getRecommendedCurrency({
      availablePaymentMethods,
      paymentVendor: PaymentVendor.Klarna,
      userCurrency: selectedCurrencyCode,
    });

    const shouldChangeCurrency = recommendedCurrency !== selectedCurrencyCode;

    if (shouldChangeCurrency) {
      setShowChangeCurrencyModal(true);
    } else {
      void createKlarnaSession({
        countryCode,
        email: user?.email,
        orderNumber: number.toString(),
      });
    }
  };

  useEffect(() => {
    if (shouldShowTagline && localizedFinalPriceCents?.amount) {
      window.Klarna?.OnsiteMessaging?.refresh();
    }
  }, [shouldShowTagline, localizedFinalPriceCents?.amount]);

  return (
    <Klarna.Wrapper data-qa="checkout_payment_klarna_component">
      {showChangeCurrencyModal && (
        // To replace this with regular old ChangeCurrencyModal
        <ChangeCurrencyKlarnaModal selectPaymentVendor={selectPaymentVendor} />
      )}
      <GiftCard />
      {shouldShowTagline && (
        <KlarnaPlacement
          country={countryCode}
          region={String(region)}
          purchaseAmount={localizedFinalPriceCents?.amount}
        />
      )}
      <p>
        {t(
          `${TP}.klarnaInstructions`,
          'You will be redirected to Klarna to complete your purchase securely.',
        )}
      </p>
      <p>
        <Trans
          i18nKey={`${TP}.klarnaTerms`}
          defaults="By proceeding, you confirm you have read and agree to the <br> <purchasePolicyLink>Purchases and Returns Policy</purchasePolicyLink>."
          components={{
            purchasePolicyLink: (
              <Klarna.Link href={getRedirectionPath('/returns')} target="_blank" rel="noreferrer" />
            ),
          }}
        />
      </p>
      <Button data-qa="checkout_continue_klarna" onClick={handleContinue} loading={isKlarnaLoading}>
        {t(`${TP}.klarnaCTA`, 'Place Order with Klarna')}
      </Button>
    </Klarna.Wrapper>
  );
};

Klarna.Wrapper = styled.div`
  width: 100%;

  p {
    margin-bottom: 16px;
  }
  button {
    margin-top: 48px;
  }
`;

Klarna.Link = styled.a`
  text-decoration: underline;
`;
